import { StandardEvents, CartDiscountUpdateEvent } from '@shopify/events';

const REGION_LABELS = Object.freeze({
  all: '全部地區',
  japan: '日本',
  korea: '韓國',
  'china-hk-macau': '中港澳',
  'southeast-asia': '東南亞',
  europe: '歐洲',
  'americas-oceania': '美洲／大洋洲',
  other: '其他地區',
  'non-china-ip': '非中國IP連線商品',
});

const TYPE_LABELS = Object.freeze({
  all: '全部類型',
  'seven-eleven': '7-11超快取',
  sim: 'SIM',
  esim: 'eSIM',
});

const NON_CHINA_IP_COLLECTION_PATH = '/collections/multi-country-dedicated-ip';

const REGION_RULES = Object.freeze({
  japan: {
    fragments: ['country_japan'],
    exact: ['711-jp'],
  },
  korea: {
    fragments: ['country_korea'],
    exact: ['711-kr'],
  },
  'china-hk-macau': {
    fragments: ['china_hk_macau', 'country_china', 'country_hongkong', 'country_hong_kong', 'country_macau'],
    exact: ['711-ch'],
  },
  'southeast-asia': {
    fragments: [
      'southeast asia',
      'country_sea',
      'country_thailand',
      'country_cambodia',
      'country_vietnam',
      'country_malaysia',
      'country_philippines',
      'country_singapore',
      'country_indonesia',
      'country_laos',
      'country_myanmar',
      'country_brunei',
    ],
    exact: ['711-xmytv'],
  },
  europe: {
    fragments: ['country_europe', 'more_eu_esim', 'country_russia'],
    exact: ['europe'],
  },
  'americas-oceania': {
    fragments: [
      'country_usa',
      'country_canada',
      'country_mexico',
      'country_north_america',
      'country_central_america',
      'country_south_america',
      'country_guam',
      'country_saipan',
      'esim_oceania',
      'sim_oceania',
      'country_australia',
      'country_new_zealand',
    ],
    exact: ['sim-naa', 'esim-naa', 'usa', 'naa', 'mcu', 'emcu', 'eus', 'esaa', 'saa', 'ssa', '711-us', 'oceania', '711-oc'],
  },
});

const OTHER_REGION_RULE = Object.freeze({
  fragments: [
    'south asia',
    'country_india',
    'country_bangladesh',
    'country_sri_lanka',
    'country_srilanka',
    'country_maldives',
    'middle east',
    'country_oman',
    'country_uae',
    'country_turkey',
    'country_saudi',
    'country_qatar',
    'country_israel',
    'country_jordan',
    'africa',
    'country_egypt',
    'country_multi',
    'more_world_esim',
    'more_asia_esim',
    'esim_mg',
    'country_mongolia',
  ],
  exact: ['global', 'world'],
});

const SEARCH_ALIAS_GROUPS = Object.freeze([
  ['日本', 'japan', 'jp', 'country_japan', '711-jp'],
  ['韓國', '韩国', 'korea', 'kr', 'country_korea', '711-kr'],
  ['中港澳', '中國', '中国', 'china', '香港', 'hongkong', 'hong kong', '澳門', '澳门', 'macau', 'china_hk_macau', '711-ch'],
  [
    '東南亞',
    '东南亚',
    'southeast asia',
    'country_sea',
    '泰國',
    '泰国',
    'thailand',
    '越南',
    'vietnam',
    '馬來西亞',
    '马来西亚',
    'malaysia',
    '新加坡',
    'singapore',
    '印尼',
    'indonesia',
    '菲律賓',
    '菲律宾',
    'philippines',
    '柬埔寨',
    'cambodia',
    '711-xmytv',
  ],
  ['歐洲', '欧洲', '歐美', '欧美', 'europe', 'eu', '俄羅斯', '俄罗斯', 'russia', 'country_europe', 'country_russia', 'more_eu_esim'],
  ['美洲', 'america', 'americas', 'naa', 'mcu', 'emcu', 'eus', 'esaa', 'saa', 'ssa', '711-us'],
  ['北美', 'north america', 'naa', 'esim-naa', 'sim-naa'],
  ['中美洲', 'central america', 'mcu', 'emcu'],
  ['南美', 'south america', 'esaa', 'saa', 'ssa'],
  ['美國', '美国', 'usa', 'united states', 'country_usa', '711-us'],
  ['大洋洲', 'oceania', 'esim_oceania', 'sim_oceania', '711-oc'],
  ['澳洲', 'australia', 'country_australia'],
  ['紐西蘭', '新西兰', 'new zealand', 'country_new_zealand'],
  ['非中國ip', '非中国ip', '非中國ip連線', '非中国ip连线', 'non china ip', 'non-china-ip', 'multi-country-dedicated-ip'],
]);

class GroupBuyCollection extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.dataset.initialized = 'true';
    this.pageRoot = this.ownerDocument;
    this.grid = this.querySelector('[data-group-buy-grid]');
    this.loadingStatus = this.querySelector('[data-loading-status]');
    this.retryButton = this.querySelector('[data-load-retry]');
    this.filterEmpty = this.querySelector('[data-filter-empty]');
    this.pagination = this.querySelector('[data-group-buy-pagination]');
    this.productsPerPage = Number(this.dataset.productsPerPage || 0);
    this.currentProductPage = 1;
    this.variantCache = new WeakMap();
    this.productDetailsCache = new Map();
    this.purchaseAnimations = new WeakMap();
    this.modal = this.querySelector('[data-product-modal]');
    this.modalRequestToken = 0;
    this.modalGalleryIndex = 0;
    this.modalPointerStart = null;
    this.activeModalCard = null;
    this.activeModalForm = null;
    this.modalTrigger = null;
    this.activeRegion = 'all';
    this.activeType = 'all';
    this.searchQuery = '';
    this.nonChinaIpHandles = new Set();
    this.nonChinaIpLoading = true;
    this.pageCount = Number(this.dataset.pageCount || 1);
    this.nextPage = 2;
    this.failedPages = [];
    this.catalogLoading = this.pageCount > 1;

    this.refreshExternalControls();
    this.pageRoot.addEventListener('change', this.handleVariantChange);
    this.pageRoot.addEventListener('click', this.handleFilterClick);
    this.pageRoot.addEventListener('input', this.handleSearchInput);
    this.pageRoot.addEventListener('submit', this.handleFormSubmit);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate);
    document.addEventListener(StandardEvents.cartError, this.handleCartError);
    document.addEventListener('shopify:section:load', this.handleThemeEditorUpdate);
    document.addEventListener('shopify:section:unload', this.handleThemeEditorUpdate);
    document.addEventListener('shopify:section:reorder', this.handleThemeEditorUpdate);
    this.modal?.addEventListener('cancel', this.handleModalCancel);
    this.modal?.addEventListener('close', this.handleModalClose);
    this.modal?.addEventListener('keydown', this.handleModalKeydown);
    this.modal?.addEventListener('pointerdown', this.handleModalPointerDown);
    this.modal?.addEventListener('pointerup', this.handleModalPointerUp);
    this.modal?.addEventListener('pointercancel', this.handleModalPointerCancel);

    this.setFilterControlsLoading(true);
    this.initializeCards();
    this.sortCards();
    this.applyFilter();
    this.applyDiscount();
    this.loadNonChinaIpProducts();

    if (this.pageCount > 1) {
      this.loadRemainingPages();
    } else {
      this.catalogLoading = false;
      this.setFilterControlsLoading(this.isFilterDataLoading());
      this.applyFilter();
      this.setLoadingMessage('全部商品已載入。');
    }
  }

  disconnectedCallback() {
    this.pageRoot?.removeEventListener('change', this.handleVariantChange);
    this.pageRoot?.removeEventListener('click', this.handleFilterClick);
    this.pageRoot?.removeEventListener('input', this.handleSearchInput);
    this.pageRoot?.removeEventListener('submit', this.handleFormSubmit);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate);
    document.removeEventListener(StandardEvents.cartError, this.handleCartError);
    document.removeEventListener('shopify:section:load', this.handleThemeEditorUpdate);
    document.removeEventListener('shopify:section:unload', this.handleThemeEditorUpdate);
    document.removeEventListener('shopify:section:reorder', this.handleThemeEditorUpdate);
    this.modal?.removeEventListener('cancel', this.handleModalCancel);
    this.modal?.removeEventListener('close', this.handleModalClose);
    this.modal?.removeEventListener('keydown', this.handleModalKeydown);
    this.modal?.removeEventListener('pointerdown', this.handleModalPointerDown);
    this.modal?.removeEventListener('pointerup', this.handleModalPointerUp);
    this.modal?.removeEventListener('pointercancel', this.handleModalPointerCancel);
    document.documentElement.classList.remove('group-buy-modal-open');
    this.discountController?.abort();
    this.discountRequest = null;
    delete this.dataset.initialized;
  }

  refreshExternalControls() {
    this.cartCount = this.pageRoot?.querySelector('[data-group-buy-cart-count]');
    this.discountStatus = this.pageRoot?.querySelector('[data-discount-status]');
    this.regionButtons = Array.from(this.pageRoot?.querySelectorAll('[data-group-buy-region]') || []);
    this.typeButtons = Array.from(this.pageRoot?.querySelectorAll('[data-group-buy-type]') || []);
    this.typeFilterWrap = this.pageRoot?.querySelector('[data-type-filter-wrap]');
    this.filterStatus = this.pageRoot?.querySelector('[data-filter-status]');
    this.searchInput = this.pageRoot?.querySelector('[data-product-search]');

    if (this.regionButtons.length === 0) {
      this.activeRegion = 'all';
      this.activeType = 'all';
    } else {
      this.updatePressedButtons(
        this.regionButtons,
        this.regionButtons.find((button) => button.dataset.groupBuyRegion === this.activeRegion) || this.regionButtons[0]
      );
    }

    if (this.typeButtons.length === 0) {
      this.activeType = 'all';
    } else {
      if (this.activeRegion === 'all' || (this.activeRegion === 'non-china-ip' && this.activeType === 'all')) {
        this.activeType = 'all';
        this.updatePressedButtons(this.typeButtons, null);
      } else {
        const activeTypeButton = this.typeButtons.find((button) => button.dataset.groupBuyType === this.activeType);
        if (!activeTypeButton) this.activeType = 'esim';
        this.updatePressedButtons(
          this.typeButtons,
          this.typeButtons.find((button) => button.dataset.groupBuyType === this.activeType) || this.typeButtons[0]
        );
      }
    }

    if (!this.searchInput) this.searchQuery = '';
    if (this.typeFilterWrap) this.typeFilterWrap.hidden = this.activeRegion === 'all';
  }

  handleThemeEditorUpdate = () => {
    window.requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.refreshExternalControls();
      this.setFilterControlsLoading(this.isFilterDataLoading());
      this.applyFilter();
      this.applyDiscount();
    });
  };

  handleVariantChange = (event) => {
    const select = event.target.closest('[data-option-select]');
    if (!select || !this.contains(select)) return;

    const card = select.closest('[data-group-buy-card]');
    if (!card) return;

    this.updateOptionAvailability(card);
  };

  handleFilterClick = (event) => {
    const pageButton = event.target.closest('[data-group-buy-page-step]');
    if (pageButton && this.pagination?.contains(pageButton)) {
      if (pageButton.disabled) return;
      event.preventDefault();
      this.currentProductPage += Number(pageButton.dataset.groupBuyPageStep);
      this.applyFilter();
      this.grid?.focus({ preventScroll: true });
      this.grid?.scrollIntoView({ block: 'start', behavior: 'auto' });
      return;
    }

    const cartLink = event.target.closest('[data-group-buy-cart-link]');
    if (cartLink && cartLink.dataset.discountCode === this.dataset.discountCode) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.navigateToDiscountCart(cartLink);
      return;
    }

    if (this.modal && event.target === this.modal) {
      this.closeProductModal();
      return;
    }

    const modalClose = event.target.closest('[data-modal-close]');
    if (modalClose && this.modal?.contains(modalClose)) {
      this.closeProductModal();
      return;
    }

    const modalRetry = event.target.closest('[data-modal-retry]');
    if (modalRetry && this.modal?.contains(modalRetry)) {
      this.loadActiveProductDetails();
      return;
    }

    const modalConfirm = event.target.closest('[data-modal-confirm]');
    if (modalConfirm && this.modal?.contains(modalConfirm)) {
      this.confirmModalPurchase();
      return;
    }

    const modalPrevious = event.target.closest('[data-modal-gallery-previous]');
    if (modalPrevious && this.modal?.contains(modalPrevious)) {
      this.selectModalImage(this.modalGalleryIndex - 1);
      return;
    }

    const modalNext = event.target.closest('[data-modal-gallery-next]');
    if (modalNext && this.modal?.contains(modalNext)) {
      this.selectModalImage(this.modalGalleryIndex + 1);
      return;
    }

    const modalDot = event.target.closest('[data-modal-gallery-dot]');
    if (modalDot && this.modal?.contains(modalDot)) {
      this.selectModalImage(Number(modalDot.dataset.modalGalleryDot || 0));
      return;
    }

    const productConfirmButton = event.target.closest('[data-product-confirm-open]');
    if (productConfirmButton && this.contains(productConfirmButton)) {
      this.openProductModal(productConfirmButton);
      return;
    }

    const purchaseToggle = event.target.closest('.group-buy-card__purchase-toggle');
    if (purchaseToggle && this.contains(purchaseToggle)) {
      event.preventDefault();
      const purchaseDetails = purchaseToggle.closest('.group-buy-card__purchase');
      if (purchaseDetails) this.togglePurchaseDetails(purchaseDetails);
      return;
    }

    const quantityButton = event.target.closest('[data-quantity-change]');
    if (quantityButton && this.contains(quantityButton)) {
      this.adjustQuantity(quantityButton);
      return;
    }

    const cardOpenButton = event.target.closest('[data-card-open-options]');
    if (cardOpenButton && this.contains(cardOpenButton)) {
      const card = cardOpenButton.closest('[data-group-buy-card]');
      const purchaseDetails = card?.querySelector('.group-buy-card__purchase');
      if (purchaseDetails) {
        this.togglePurchaseDetails(purchaseDetails, true);
        this.setCardStatus(card, '選購規格已展開。');
      }
      return;
    }

    const retryButton = event.target.closest('[data-load-retry]');
    if (retryButton && this.contains(retryButton)) {
      this.handleRetry();
      return;
    }

    const regionButton = event.target.closest('[data-group-buy-region]');
    if (regionButton) {
      this.refreshExternalControls();
      this.activeRegion = regionButton.dataset.groupBuyRegion || 'all';
      this.activeType = ['all', 'non-china-ip'].includes(this.activeRegion) ? 'all' : 'esim';
      this.updatePressedButtons(this.regionButtons, regionButton);
      this.updatePressedButtons(
        this.typeButtons,
        ['all', 'non-china-ip'].includes(this.activeRegion)
          ? null
          : this.typeButtons.find((button) => button.dataset.groupBuyType === this.activeType)
      );
      if (this.typeFilterWrap) this.typeFilterWrap.hidden = this.activeRegion === 'all';
      this.applyFilter();
      return;
    }

    const typeButton = event.target.closest('[data-group-buy-type]');
    if (!typeButton || this.activeRegion === 'all') return;

    this.refreshExternalControls();
    this.activeType = typeButton.dataset.groupBuyType || 'esim';
    this.updatePressedButtons(this.typeButtons, typeButton);
    this.applyFilter();
  };

  handleModalCancel = (event) => {
    event.preventDefault();
    this.closeProductModal();
  };

  handleModalClose = () => {
    document.documentElement.classList.remove('group-buy-modal-open');
    this.modalRequestToken += 1;
    const trigger = this.modalTrigger;
    this.modalTrigger = null;
    this.activeModalCard = null;
    this.activeModalForm = null;
    window.requestAnimationFrame(() => trigger?.focus());
  };

  handleModalKeydown = (event) => {
    if (event.key === 'ArrowLeft' && event.target.closest('[data-modal-gallery]')) {
      event.preventDefault();
      this.selectModalImage(this.modalGalleryIndex - 1);
      return;
    }

    if (event.key === 'ArrowRight' && event.target.closest('[data-modal-gallery]')) {
      event.preventDefault();
      this.selectModalImage(this.modalGalleryIndex + 1);
      return;
    }

    if (event.key !== 'Tab') return;
    const focusable = this.getModalFocusableElements();
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  handleModalPointerDown = (event) => {
    if (event.pointerType === 'mouse' || !event.target.closest('[data-modal-gallery-stage]')) return;
    this.modalPointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
  };

  handleModalPointerUp = (event) => {
    if (!this.modalPointerStart || event.pointerId !== this.modalPointerStart.id) return;
    const distanceX = event.clientX - this.modalPointerStart.x;
    const distanceY = event.clientY - this.modalPointerStart.y;
    this.modalPointerStart = null;
    if (Math.abs(distanceX) < 48 || Math.abs(distanceX) <= Math.abs(distanceY) * 1.2) return;
    this.selectModalImage(this.modalGalleryIndex + (distanceX < 0 ? 1 : -1));
  };

  handleModalPointerCancel = () => {
    this.modalPointerStart = null;
  };

  getModalFocusableElements() {
    if (!this.modal) return [];
    return Array.from(
      this.modal.querySelectorAll('button:not([disabled]):not([hidden]), a[href], [tabindex]:not([tabindex="-1"])')
    ).filter((element) => !element.closest('[hidden]'));
  }

  async openProductModal(button) {
    if (!this.modal || button.disabled) return;
    const card = button.closest('[data-group-buy-card]');
    const form = card?.querySelector('[data-group-buy-form]');
    const handle = card?.dataset.productHandle?.trim();
    if (!card || !form || !handle) return;

    this.modalTrigger = button;
    this.activeModalCard = card;
    this.activeModalForm = form;
    this.modal.querySelector('[data-modal-title]').textContent = card.dataset.productTitle || '商品資訊';
    this.updateModalSummary(card);
    this.setModalActionError('');
    this.resetModalConfirmButton();
    this.setModalLoadState('loading');
    document.documentElement.classList.add('group-buy-modal-open');
    if (typeof this.modal.showModal === 'function') this.modal.showModal();
    else this.modal.setAttribute('open', '');
    this.modal.querySelector('[data-modal-close]')?.focus();
    await this.loadActiveProductDetails();
  }

  closeProductModal() {
    if (!this.modal) return;
    if (typeof this.modal.close === 'function' && this.modal.open) {
      this.modal.close();
      return;
    }
    this.modal.removeAttribute('open');
    this.handleModalClose();
  }

  async loadActiveProductDetails() {
    const card = this.activeModalCard;
    const handle = card?.dataset.productHandle?.trim();
    if (!card || !handle || !this.modal) return;

    const requestToken = ++this.modalRequestToken;
    this.setModalLoadState('loading');
    this.setModalActionError('');

    try {
      const product = await this.fetchProductDetails(handle);
      if (requestToken !== this.modalRequestToken || card !== this.activeModalCard) return;
      this.renderModalProduct(product, card);
      this.setModalLoadState('ready');
      const submitButton = this.activeModalForm?.querySelector('[data-add-button]');
      const confirmButton = this.modal.querySelector('[data-modal-confirm]');
      if (confirmButton) confirmButton.disabled = !submitButton || submitButton.disabled;
    } catch (error) {
      if (requestToken !== this.modalRequestToken || card !== this.activeModalCard) return;
      console.error('Unable to load group-buy product details.', error);
      this.setModalLoadState('error');
      const status = this.modal.querySelector('[data-modal-status]');
      if (status) status.textContent = '商品資訊載入失敗，可使用重新載入按鈕再試一次。';
    }
  }

  fetchProductDetails(handle) {
    if (this.productDetailsCache.has(handle)) return this.productDetailsCache.get(handle);
    const root = window.Shopify?.routes?.root || '/';
    const request = fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((product) => {
        if (!product || typeof product !== 'object') throw new Error('Invalid product response');
        return product;
      })
      .catch((error) => {
        this.productDetailsCache.delete(handle);
        throw error;
      });
    this.productDetailsCache.set(handle, request);
    return request;
  }

  setModalLoadState(state) {
    if (!this.modal) return;
    const loading = this.modal.querySelector('[data-modal-loading]');
    const error = this.modal.querySelector('[data-modal-load-error]');
    const content = this.modal.querySelector('[data-modal-content]');
    const confirm = this.modal.querySelector('[data-modal-confirm]');
    if (loading) loading.hidden = state !== 'loading';
    if (error) error.hidden = state !== 'error';
    if (content) content.hidden = state !== 'ready';
    if (confirm) confirm.disabled = state !== 'ready';
    const status = this.modal.querySelector('[data-modal-status]');
    if (status) {
      status.textContent = state === 'loading'
        ? '正在載入商品圖片與詳細說明。'
        : state === 'ready'
          ? '商品資訊已載入完成。'
          : status.textContent;
    }
  }

  renderModalProduct(product, card) {
    if (!this.modal) return;
    const title = this.modal.querySelector('[data-modal-title]');
    if (title) title.textContent = product.title || card.dataset.productTitle || '商品資訊';
    this.renderModalGallery(product.images || [], product.title || card.dataset.productTitle || '商品圖片');
    const description = this.modal.querySelector('[data-modal-description]');
    if (description) {
      const fragment = this.sanitizeProductDescription(product.description || '');
      description.replaceChildren(fragment);
      if (!description.textContent.trim() && !description.querySelector('img')) {
        const emptyMessage = document.createElement('p');
        emptyMessage.textContent = '此商品目前沒有額外說明。';
        description.append(emptyMessage);
      }
    }
    this.updateModalSummary(card);
  }

  sanitizeProductDescription(html) {
    const parsed = new DOMParser().parseFromString(String(html), 'text/html');
    parsed.querySelectorAll('script, style, iframe, object, embed, form, input, button, select, textarea, base, meta, link').forEach((node) => node.remove());
    for (const element of parsed.body.querySelectorAll('*')) {
      for (const attribute of Array.from(element.attributes)) {
        const name = attribute.name.toLowerCase();
        const value = attribute.value.trim();
        if (name.startsWith('on') || ['srcdoc', 'formaction', 'action', 'integrity'].includes(name)) {
          element.removeAttribute(attribute.name);
          continue;
        }
        if (name === 'id') {
          element.removeAttribute(attribute.name);
          continue;
        }
        if (name === 'style' && /(expression\s*\(|javascript:|@import|-moz-binding|behavior\s*:)/i.test(value)) {
          element.removeAttribute(attribute.name);
          continue;
        }
        if (['href', 'src', 'poster', 'xlink:href'].includes(name) && !this.isSafeProductUrl(value, name !== 'href')) {
          element.removeAttribute(attribute.name);
          continue;
        }
        if (name === 'srcset') element.removeAttribute(attribute.name);
      }
      if (element.tagName === 'A' && element.hasAttribute('href')) {
        element.setAttribute('rel', 'noopener noreferrer');
      }
    }
    const fragment = document.createDocumentFragment();
    while (parsed.body.firstChild) fragment.append(parsed.body.firstChild);
    return fragment;
  }

  isSafeProductUrl(value, isMedia = false) {
    if (!value) return true;
    if (value.startsWith('#') || value.startsWith('/') || value.startsWith('./') || value.startsWith('../')) return true;
    try {
      const url = new URL(value, window.location.origin);
      if (url.protocol === 'http:' || url.protocol === 'https:') return true;
      return !isMedia && ['mailto:', 'tel:'].includes(url.protocol);
    } catch (_error) {
      return false;
    }
  }

  renderModalGallery(images, title) {
    if (!this.modal) return;
    const stage = this.modal.querySelector('[data-modal-gallery-stage]');
    const dots = this.modal.querySelector('[data-modal-gallery-dots]');
    const previous = this.modal.querySelector('[data-modal-gallery-previous]');
    const next = this.modal.querySelector('[data-modal-gallery-next]');
    if (!stage || !dots) return;
    stage.replaceChildren();
    dots.replaceChildren();

    const safeImages = Array.from(images)
      .map((image) => (typeof image === 'string' ? image : image?.src))
      .filter((source) => typeof source === 'string' && this.isSafeProductUrl(source, true));

    safeImages.forEach((source, index) => {
      const image = document.createElement('img');
      image.className = 'group-buy-product-modal__image';
      image.src = source.startsWith('//') ? `https:${source}` : source;
      image.alt = `${title}－第 ${index + 1} 張圖片`;
      image.loading = index === 0 ? 'eager' : 'lazy';
      if (index === 0) image.fetchPriority = 'high';
      image.dataset.modalGalleryImage = String(index);
      image.hidden = index !== 0;
      stage.append(image);

      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'group-buy-product-modal__gallery-dot';
      dot.dataset.modalGalleryDot = String(index);
      dot.setAttribute('aria-label', `顯示第 ${index + 1} 張商品圖片`);
      dots.append(dot);
    });

    if (safeImages.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'group-buy-product-modal__gallery-empty';
      empty.textContent = '此商品目前沒有可顯示的圖片';
      stage.append(empty);
    }

    const multipleImages = safeImages.length > 1;
    if (previous) previous.hidden = !multipleImages;
    if (next) next.hidden = !multipleImages;
    dots.hidden = !multipleImages;
    this.modalGalleryIndex = 0;
    this.selectModalImage(0, false);
  }

  selectModalImage(index, announce = true) {
    if (!this.modal) return;
    const images = Array.from(this.modal.querySelectorAll('[data-modal-gallery-image]'));
    const dots = Array.from(this.modal.querySelectorAll('[data-modal-gallery-dot]'));
    if (images.length === 0) return;
    const nextIndex = ((index % images.length) + images.length) % images.length;
    this.modalGalleryIndex = nextIndex;
    images.forEach((image, imageIndex) => {
      image.hidden = imageIndex !== nextIndex;
    });
    dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === nextIndex;
      dot.classList.toggle('is-active', isActive);
      if (isActive) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    const status = this.modal.querySelector('[data-modal-gallery-status]');
    if (announce && status) status.textContent = `目前顯示第 ${nextIndex + 1} 張，共 ${images.length} 張。`;
  }

  updateModalSummary(card) {
    if (!this.modal || !card) return;
    const summary = this.modal.querySelector('[data-modal-summary]');
    if (!summary) return;
    summary.replaceChildren();
    const rows = [];
    for (const select of card.querySelectorAll('[data-option-select]')) {
      const label = select.closest('[data-option-field]')?.querySelector('label')?.textContent?.trim() || '規格';
      const value = select.selectedOptions[0]?.textContent?.trim() || select.value;
      if (value && value !== 'Default Title') rows.push([label, value]);
    }

    const quantity = Math.max(1, Number(card.querySelector('input[name="quantity"]')?.value || 1));
    const variant = this.getSelectedCardVariant(card);
    rows.push(['數量', String(quantity)]);
    rows.push(['單價', variant?.price || card.querySelector('[data-original-price]')?.textContent?.trim() || '—']);
    const discountCents = Number(variant?.discountPriceCents);
    const estimatedTotal = Number.isFinite(discountCents)
      ? this.formatMoney(discountCents * quantity)
      : card.querySelector('[data-discount-price]')?.textContent?.trim() || '—';
    rows.push(['預估折扣後總額', estimatedTotal]);

    for (const [label, value] of rows) {
      const term = document.createElement('dt');
      const description = document.createElement('dd');
      term.textContent = label;
      description.textContent = value;
      summary.append(term, description);
    }
  }

  formatMoney(cents) {
    const amount = Number(cents || 0);
    const format = this.dataset.moneyFormat || '${{amount_no_decimals}}';
    const withDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      const parts = (number / 100).toFixed(precision).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
    };
    const match = format.match(/\{\{\s*(amount(?:_no_decimals|_with_comma_separator|_no_decimals_with_comma_separator|_with_apostrophe_separator)?)\s*\}\}/);
    if (!match) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'zh-TW', {
          style: 'currency',
          currency: this.dataset.currencyCode || 'TWD',
          maximumFractionDigits: 0,
        }).format(amount / 100);
      } catch (_error) {
        return `$${Math.round(amount / 100)}`;
      }
    }
    const replacements = {
      amount: withDelimiters(amount, 2),
      amount_no_decimals: withDelimiters(amount, 0),
      amount_with_comma_separator: withDelimiters(amount, 2, '.', ','),
      amount_no_decimals_with_comma_separator: withDelimiters(amount, 0, '.', ','),
      amount_with_apostrophe_separator: withDelimiters(amount, 2, "'", '.'),
    };
    return format.replace(match[0], replacements[match[1]]).replace(/&nbsp;/g, ' ');
  }

  confirmModalPurchase() {
    if (!this.modal || !this.activeModalForm) return;
    const confirm = this.modal.querySelector('[data-modal-confirm]');
    const submit = this.activeModalForm.querySelector('[data-add-button]');
    if (!confirm || confirm.disabled || !submit || submit.disabled) return;
    const variant = this.getSelectedCardVariant(this.activeModalCard);
    const variantInput = this.activeModalForm.querySelector('input[name="id"]');
    if (!variant?.available || !variantInput) {
      this.setModalActionError('目前選擇的規格無法購買，請關閉視窗重新選擇。');
      return;
    }
    variantInput.value = String(variant.id);
    variantInput.setAttribute('value', String(variant.id));
    this.setModalActionError('');
    confirm.disabled = true;
    confirm.textContent = '正在加入購物車…';
    this.activeModalForm.requestSubmit(submit);
  }

  getSelectedCardVariant(card) {
    if (!card) return null;
    const variants = this.variantCache.get(card) || [];
    const selections = Array.from(card.querySelectorAll('[data-option-select]')).map((select) => ({
      position: Number(select.dataset.optionPosition),
      value: select.value,
    }));
    return variants.find((variant) => selections.every((selection) => variant.options[selection.position] === selection.value))
      || variants.find((variant) => variant.available)
      || variants[0]
      || null;
  }

  setModalActionError(message) {
    if (!this.modal) return;
    const error = this.modal.querySelector('[data-modal-action-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  resetModalConfirmButton() {
    if (!this.modal) return;
    const confirm = this.modal.querySelector('[data-modal-confirm]');
    if (!confirm) return;
    confirm.textContent = confirm.dataset.defaultLabel || '確認並加入購物車';
    const submit = this.activeModalForm?.querySelector('[data-add-button]');
    confirm.disabled = !submit || submit.disabled;
  }

  togglePurchaseDetails(details, forceOpen = null) {
    if (!details || details.dataset.animating === 'true') return;

    const panel = details.querySelector('.group-buy-card__purchase-panel');
    const shouldOpen = forceOpen == null ? !details.open : forceOpen;
    if (shouldOpen === details.open || !panel) return;

    const animationStyle = this.dataset.purchaseAnimation || 'slide';
    const duration = Number(this.dataset.purchaseAnimationDuration || 240);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (animationStyle === 'none' || reduceMotion || typeof panel.animate !== 'function') {
      details.open = shouldOpen;
      return;
    }

    if (shouldOpen) details.open = true;
    const panelHeight = panel.scrollHeight;
    const collapsedTransform = animationStyle === 'pop'
      ? 'translateY(-10px) scale(0.96)'
      : 'translateY(-8px)';
    const openFrame = { height: `${panelHeight}px`, opacity: 1, transform: 'translateY(0) scale(1)' };
    const closedFrame = { height: '0px', opacity: 0, transform: collapsedTransform };
    const keyframes = shouldOpen ? [closedFrame, openFrame] : [openFrame, closedFrame];

    details.dataset.animating = 'true';
    panel.style.overflow = 'hidden';
    const animation = panel.animate(keyframes, {
      duration: Math.max(0, duration),
      easing: animationStyle === 'pop' ? 'cubic-bezier(0.2, 0.9, 0.25, 1.2)' : 'cubic-bezier(0.22, 1, 0.36, 1)',
    });
    this.purchaseAnimations.set(details, animation);

    animation.finished
      .catch(() => {})
      .finally(() => {
        if (!shouldOpen) details.open = false;
        panel.style.removeProperty('overflow');
        details.removeAttribute('data-animating');
        this.purchaseAnimations.delete(details);
      });
  }

  updatePressedButtons(buttons, activeButton) {
    for (const button of buttons) {
      const isActive = button === activeButton;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    }
  }

  handleSearchInput = (event) => {
    const searchInput = event.target.closest('[data-product-search]');
    if (!searchInput) return;

    this.searchInput = searchInput;
    this.searchQuery = this.normalizeSearchText(searchInput.value);
    this.applyFilter();
  };

  handleFormSubmit = (event) => {
    const form = event.target.closest('[data-group-buy-form]');
    if (!form || !this.contains(form)) return;

    const card = form.closest('[data-group-buy-card]');
    const button = form.querySelector('[data-add-button]');
    if (!card || !button || button.disabled) return;

    card.classList.add('is-adding');
    this.setCardStatus(card, '正在加入購物車…');
    if (card === this.activeModalCard) {
      const confirm = this.modal?.querySelector('[data-modal-confirm]');
      if (confirm) {
        confirm.disabled = true;
        confirm.textContent = '正在加入購物車…';
      }
    }
    queueMicrotask(() => {
      button.disabled = true;
    });

    window.setTimeout(() => {
      if (!card.classList.contains('is-adding')) return;
      card.classList.remove('is-adding');
      button.disabled = false;
      this.setCardStatus(card, '連線逾時，請重試。');
      if (card === this.activeModalCard) {
        this.setModalActionError('連線逾時，請重試。');
        this.resetModalConfirmButton();
      }
    }, 12000);
  };

  // 4.4 dispatches before the request finishes; the settled promise owns the result.
  handleCartUpdate = async (event) => {
    const sourceCard = event.target?.closest?.('[data-group-buy-card]');
    const card = sourceCard && this.contains(sourceCard) ? sourceCard : null;
    try {
      const result = await event.promise;
      if (!result || !this.isConnected) return;
      const { cart, detail } = result;
      this.refreshExternalControls();
      const itemCount = cart?.totalQuantity ?? detail?.items?.reduce((sum, item) => sum + item.quantity, 0);
      if (this.cartCount && Number.isFinite(itemCount)) this.cartCount.textContent = String(itemCount);
      if (card) {
        card.classList.remove('is-adding');
        const button = card.querySelector('[data-add-button]');
        if (button) button.disabled = false;
        if (detail?.didError || result.userErrors?.length) {
          this.setCardStatus(card, '無法加入指定數量，請調整後重試。');
          if (card === this.activeModalCard) {
            this.setModalActionError('無法加入指定數量，請關閉視窗調整後再試。');
            this.resetModalConfirmButton();
          }
          return;
        }
        this.setCardStatus(card, '已加入購物車！');
        if (card === this.activeModalCard) this.closeProductModal();
      }
      await this.applyDiscount();
    } catch (error) {
      if (!this.isConnected || error?.name === 'AbortError') return;
      if (card) this.showCartError(card, '加入購物車失敗，請重試。');
    }
  };

  handleCartError = (event) => {
    const card = event.target?.closest?.('[data-group-buy-card]');
    if (card && this.contains(card)) this.showCartError(card, event.error || '加入購物車失敗，請重試。');
  };

  showCartError(card, message) {
    card.classList.remove('is-adding');
    const button = card.querySelector('[data-add-button]');
    if (button) button.disabled = false;
    this.setCardStatus(card, message);
    if (card === this.activeModalCard) {
      this.setModalActionError(message);
      this.resetModalConfirmButton();
    }
  }

  handleRetry = () => {
    this.retryButton.hidden = true;
    this.loadRemainingPages(this.failedPages);
  };

  async loadRemainingPages(retryPages = []) {
    const pages = retryPages.length > 0
      ? [...retryPages]
      : Array.from({ length: Math.max(this.pageCount - 1, 0) }, (_, index) => index + 2);
    if (pages.length === 0) return;

    this.catalogLoading = true;
    this.setFilterControlsLoading(true);
    this.setLoadingMessage(`正在同時載入其餘 ${pages.length} 頁商品…`);

    const results = await Promise.allSettled(pages.map((page) => this.fetchCatalogPage(page)));
    const existingIds = new Set(
      Array.from(this.querySelectorAll('[data-group-buy-card]')).map((card) => card.dataset.productId)
    );
    const failedPages = [];

    results.forEach((result, index) => {
      const page = pages[index];
      if (result.status === 'rejected') {
        console.error(`Unable to load group-buy product page ${page}.`, result.reason);
        failedPages.push(page);
        return;
      }

      for (const incomingCard of result.value) {
        if (existingIds.has(incomingCard.dataset.productId)) continue;
        this.grid?.append(incomingCard);
        existingIds.add(incomingCard.dataset.productId);
      }
    });

    this.initializeCards();
    this.sortCards();
    this.applyFilter();

    if (failedPages.length > 0) {
      this.failedPages = failedPages;
      this.nextPage = Math.min(...failedPages);
      this.setLoadingMessage(`仍有 ${failedPages.length} 頁商品未載入，請按重試。`);
      if (this.retryButton) this.retryButton.hidden = false;
      return;
    }

    this.failedPages = [];
    this.nextPage = this.pageCount + 1;
    this.catalogLoading = false;
    this.setFilterControlsLoading(this.isFilterDataLoading());
    this.applyFilter();
    this.setLoadingMessage('全部商品已載入，可購買商品已排在前方。');
  }

  async loadNonChinaIpProducts() {
    try {
      const url = new URL(NON_CHINA_IP_COLLECTION_PATH, window.location.origin);
      const response = await fetch(url.toString(), {
        cache: 'no-store',
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const sourceDocument = new DOMParser().parseFromString(html, 'text/html');
      const handles = new Set();

      for (const link of sourceDocument.querySelectorAll('[data-product-tabs-section] a[href*="/products/"]')) {
        const href = link.getAttribute('href');
        if (!href) continue;
        const match = new URL(href, window.location.origin).pathname.match(/^\/products\/([^/]+)/);
        if (match?.[1]) handles.add(decodeURIComponent(match[1]).toLocaleLowerCase());
      }

      if (handles.size === 0) throw new Error('No products found in the non-China IP product tabs.');
      this.nonChinaIpHandles = handles;
    } catch (error) {
      console.error('Unable to load non-China IP products; falling back to Shopify collection membership.', error);
    } finally {
      this.nonChinaIpLoading = false;
      if (!this.isConnected) return;
      this.initializeCards();
      this.sortCards();
      this.setFilterControlsLoading(this.isFilterDataLoading());
      this.applyFilter();
    }
  }

  isFilterDataLoading() {
    return this.catalogLoading || this.nonChinaIpLoading;
  }

  async fetchCatalogPage(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(documentFragment.querySelectorAll('[data-group-buy-grid] [data-group-buy-card]'));
  }

  sortCards() {
    if (!this.grid) return;

    const cards = Array.from(this.grid.querySelectorAll('[data-group-buy-card]'));
    cards
      .sort((first, second) => {
        const availabilityDifference =
          Number(second.dataset.available === 'true') - Number(first.dataset.available === 'true');
        if (availabilityDifference !== 0) return availabilityDifference;

        const priorityDifference = Number(first.dataset.sortPriority || 4) - Number(second.dataset.sortPriority || 4);
        if (priorityDifference !== 0) return priorityDifference;

        return Number(first.dataset.sortIndex || 0) - Number(second.dataset.sortIndex || 0);
      })
      .forEach((card) => this.grid.append(card));
  }

  initializeCards() {
    for (const card of this.querySelectorAll('[data-group-buy-card]')) {
      this.classifyCard(card);
      if (card.dataset.optionsInitialized === 'true') continue;

      const dataElement = card.querySelector('[data-variant-data]');
      if (!dataElement) continue;

      try {
        const variants = JSON.parse(dataElement.textContent || '[]');
        this.variantCache.set(card, variants);
        card.dataset.optionsInitialized = 'true';
        this.updateOptionAvailability(card, false);
      } catch (error) {
        console.error('Unable to initialize group-buy product options.', error);
        this.setCardStatus(card, '規格資料載入失敗，請重新整理頁面。');
      }
    }
  }

  classifyCard(card) {
    const tags = this.readCardTags(card);
    const regions = [];

    for (const [region, rule] of Object.entries(REGION_RULES)) {
      if (this.tagsMatchRule(tags, rule)) regions.push(region);
    }

    if (regions.length === 0 || this.tagsMatchRule(tags, OTHER_REGION_RULE)) {
      regions.push('other');
    }

    const collectionHandles = (card.dataset.productCollections || '').split(/\s+/).filter(Boolean);
    const productHandle = this.normalizeSearchText(card.dataset.productHandle || '');
    const isNonChinaIpProduct = this.nonChinaIpHandles.size > 0
      ? this.nonChinaIpHandles.has(productHandle)
      : collectionHandles.includes('multi-country-dedicated-ip');
    if (isNonChinaIpProduct) {
      regions.push('non-china-ip');
    }

    const productType = this.getProductType(
      tags,
      card.dataset.productTitle || '',
      card.dataset.productHandle || ''
    );
    card.dataset.regionCategories = [...new Set(regions)].join(' ');
    card.dataset.productType = productType;
    card.dataset.sortPriority = String({ esim: 1, sim: 2, 'seven-eleven': 3 }[productType] || 4);
  }

  readCardTags(card) {
    try {
      const parsedTags = JSON.parse(card.dataset.productTags || '[]');
      return Array.isArray(parsedTags) ? parsedTags.map((tag) => this.normalizeSearchText(tag)) : [];
    } catch (error) {
      console.error('Unable to read group-buy product tags.', error);
      return [];
    }
  }

  tagsMatchRule(tags, rule) {
    return tags.some(
      (tag) =>
        rule.exact.includes(tag) ||
        rule.fragments.some((fragment) => tag.includes(fragment))
    );
  }

  getProductType(tags, title, handle = '') {
    if (tags.some((tag) => tag.includes('711fast'))) return 'seven-eleven';
    if (tags.some((tag) => tag.includes('type_sim'))) return 'sim';
    if (tags.some((tag) => tag.includes('type_esim'))) return 'esim';

    const normalizedTitle = this.normalizeSearchText(`${title} ${handle}`);
    if (normalizedTitle.includes('超快取') || normalizedTitle.includes('7-eleven')) return 'seven-eleven';
    if (normalizedTitle.includes('esim')) return 'esim';
    if (normalizedTitle.includes('sim') || normalizedTitle.includes('上網卡')) return 'sim';
    return 'other';
  }

  updateOptionAvailability(card, announce = true) {
    const variants = this.variantCache.get(card) || [];
    if (variants.length === 0) return;

    const selects = Array.from(card.querySelectorAll('[data-option-select]'));
    const availableVariants = variants.filter((variant) => variant.available);
    const hasPurchasableVariants = availableVariants.length > 0;
    const candidates = availableVariants.length > 0 ? availableVariants : variants;

    for (let index = 0; index < selects.length; index += 1) {
      const select = selects[index];
      const optionPosition = Number(select.dataset.optionPosition);
      const previousSelections = selects.slice(0, index).map((previousSelect) => ({
        position: Number(previousSelect.dataset.optionPosition),
        value: previousSelect.value,
      }));

      for (const option of select.options) {
        const isAvailable = candidates.some(
          (variant) =>
            variant.options[optionPosition] === option.value &&
            previousSelections.every((selection) => variant.options[selection.position] === selection.value)
        );
        option.disabled = !isAvailable;
        option.hidden = hasPurchasableVariants && !isAvailable;
      }

      if (select.selectedOptions[0]?.disabled) {
        const firstEnabledOption = Array.from(select.options).find((option) => !option.disabled);
        if (firstEnabledOption) select.value = firstEnabledOption.value;
      }

      const enabledOptionCount = Array.from(select.options).filter((option) => !option.disabled).length;
      const optionField = select.closest('[data-option-field]');
      if (optionField) optionField.hidden = hasPurchasableVariants && enabledOptionCount <= 1;
    }

    const selectedVariant =
      candidates.find((variant) =>
        selects.every(
          (select) => variant.options[Number(select.dataset.optionPosition)] === select.value
        )
      ) || candidates[0];

    this.updateCardVariant(card, selectedVariant, announce);
  }

  updateCardVariant(card, variant, announce = true) {
    if (!variant) return;

    const variantInput = card.querySelector('input[name="id"]');
    const quantityInput = card.querySelector('input[name="quantity"]');
    const originalPrice = card.querySelector('[data-original-price]');
    const discountPrice = card.querySelector('[data-discount-price]');
    const image = card.querySelector('.group-buy-card__image');
    const addButton = card.querySelector('[data-add-button]');
    const confirmButton = card.querySelector('[data-product-confirm-open]');
    const addButtonContainer = card.querySelector('add-to-cart-component');
    const available = variant.available === true;
    const minimum = Number(variant.quantityMin || 1);
    const increment = Number(variant.quantityIncrement || 1);
    const maximum = variant.quantityMax;

    if (variantInput) variantInput.value = String(variant.id);
    if (originalPrice) originalPrice.textContent = variant.price || '';
    if (discountPrice) discountPrice.textContent = variant.discountPrice || '';

    if (quantityInput) {
      quantityInput.min = String(minimum);
      quantityInput.step = String(increment);
      quantityInput.value = String(minimum);
      if (maximum != null) quantityInput.max = String(maximum);
      else quantityInput.removeAttribute('max');
      quantityInput.disabled = !available;
    }
    this.updateQuantityButtons(card);

    if (image && variant.image) {
      image.removeAttribute('srcset');
      image.src = variant.image;
    }

    if (addButtonContainer && variant.image) {
      addButtonContainer.dataset.productVariantMedia = variant.image;
    }

    if (addButton) {
      addButton.disabled = !available;
    }

    if (confirmButton) {
      confirmButton.disabled = !available;
      confirmButton.textContent = available
        ? confirmButton.dataset.defaultLabel || '確認商品資訊'
        : '此規格已售罄';
    }

    if (announce) {
      this.setCardStatus(card, available ? '已更新規格與價格。' : '此規格目前無法購買。');
    }
  }

  adjustQuantity(button) {
    const card = button.closest('[data-group-buy-card]');
    const input = card?.querySelector('input[name="quantity"]');
    if (!card || !input || input.disabled) return;

    const direction = Number(button.dataset.quantityChange || 0);
    const minimum = Number(input.min || 1);
    const increment = Number(input.step || 1);
    const maximum = input.max ? Number(input.max) : Infinity;
    const current = Number(input.value || minimum);
    const next = Math.min(maximum, Math.max(minimum, current + direction * increment));
    input.value = String(next);
    this.updateQuantityButtons(card);
    this.setCardStatus(card, `數量已調整為 ${next}。`);
  }

  updateQuantityButtons(card) {
    const input = card.querySelector('input[name="quantity"]');
    const minusButton = card.querySelector('[data-quantity-change="-1"]');
    const plusButton = card.querySelector('[data-quantity-change="1"]');
    if (!input) return;

    const minimum = Number(input.min || 1);
    const maximum = input.max ? Number(input.max) : Infinity;
    const current = Number(input.value || minimum);
    const unavailable = input.disabled;
    if (minusButton) minusButton.disabled = unavailable || current <= minimum;
    if (plusButton) plusButton.disabled = unavailable || current >= maximum;
  }

  applyFilter() {
    this.refreshExternalControls();
    const cards = Array.from(this.querySelectorAll('[data-group-buy-card]'));
    let visibleCount = 0;

    for (const card of cards) {
      const regions = (card.dataset.regionCategories || '').split(' ').filter(Boolean);
      const matchesRegion = this.activeRegion === 'all' || regions.includes(this.activeRegion);
      const matchesType = this.activeType === 'all' || card.dataset.productType === this.activeType;
      const matches = matchesRegion && matchesType && this.cardMatchesSearch(card);
      card.hidden = !matches;
      if (matches) visibleCount += 1;
    }

    for (const output of this.pageRoot?.querySelectorAll('[data-product-count]') || []) {
      output.textContent = this.isFilterDataLoading() ? `${visibleCount}+` : String(visibleCount);
    }

    this.updateFilterCounts(cards);
    const pageSummary = this.pagination && this.productsPerPage > 0
      ? this.updateProductPagination(cards.filter((card) => !card.hidden))
      : null;

    const loadingComplete = this.nextPage > this.pageCount && !this.nonChinaIpLoading;
    if (this.filterEmpty) {
      this.filterEmpty.hidden = visibleCount > 0 || !loadingComplete;
      const searchDescription = this.searchQuery ? `關鍵字「${this.searchInput?.value.trim() || ''}」、` : '';
      const typeDescription = this.activeRegion === 'all' || this.activeType === 'all'
        ? ''
        : `、類型「${TYPE_LABELS[this.activeType]}」`;
      this.filterEmpty.textContent = `找不到符合${searchDescription}地區「${REGION_LABELS[this.activeRegion]}」${typeDescription}的商品。`;
    }
    if (this.filterStatus) {
      this.filterStatus.textContent = pageSummary ?? (this.searchQuery
        ? `已顯示 ${visibleCount} 項符合搜尋的商品。`
        : `已顯示 ${visibleCount} 項商品。`);
    }
  }

  updateProductPagination(cards) {
    const filterKey = JSON.stringify([this.activeRegion, this.activeType, this.searchQuery]);
    if (filterKey !== this.productPageFilterKey) this.currentProductPage = 1;
    this.productPageFilterKey = filterKey;

    const totalPages = Math.ceil(cards.length / this.productsPerPage);
    this.currentProductPage = Math.max(1, Math.min(this.currentProductPage, totalPages || 1));
    const start = (this.currentProductPage - 1) * this.productsPerPage;
    const end = Math.min(start + this.productsPerPage, cards.length);
    cards.forEach((card, index) => { card.hidden = index < start || index >= end; });

    this.pagination.hidden = totalPages <= 1;
    const previous = this.pagination.querySelector('[data-group-buy-page-step="-1"]');
    const next = this.pagination.querySelector('[data-group-buy-page-step="1"]');
    const status = this.pagination.querySelector('[data-group-buy-page-status]');
    previous.disabled = this.currentProductPage <= 1 || this.isFilterDataLoading();
    next.disabled = this.currentProductPage >= totalPages || this.isFilterDataLoading();
    status.textContent = String(this.currentProductPage) + ' / ' + String(totalPages || 1);
    status.setAttribute('aria-label', this.pagination.dataset.pageLabel.replace('__PAGE__', String(this.currentProductPage)));
    return String(cards.length ? start + 1 : 0) + '–' + String(end) + ' / ' + String(cards.length);
  }

  updateFilterCounts(cards) {
    if (this.isFilterDataLoading()) {
      for (const output of this.pageRoot?.querySelectorAll('[data-region-count], [data-type-count]') || []) {
        output.textContent = '…';
      }
      return;
    }

    const regionCounts = {
      all: 0,
      japan: 0,
      korea: 0,
      'china-hk-macau': 0,
      'southeast-asia': 0,
      europe: 0,
      'americas-oceania': 0,
      other: 0,
      'non-china-ip': 0,
    };
    const typeCounts = { all: 0, 'seven-eleven': 0, sim: 0, esim: 0 };

    for (const card of cards) {
      if (!this.cardMatchesSearch(card)) continue;

      regionCounts.all += 1;
      const regions = (card.dataset.regionCategories || '').split(' ').filter(Boolean);
      for (const region of regions) {
        if (Object.hasOwn(regionCounts, region)) regionCounts[region] += 1;
      }

      if (this.activeRegion !== 'all' && !regions.includes(this.activeRegion)) continue;
      typeCounts.all += 1;
      const productType = card.dataset.productType || 'other';
      if (Object.hasOwn(typeCounts, productType)) typeCounts[productType] += 1;
    }

    for (const [region, count] of Object.entries(regionCounts)) {
      const output = this.pageRoot?.querySelector(`[data-region-count="${region}"]`);
      if (output) output.textContent = String(count);
    }

    for (const [type, count] of Object.entries(typeCounts)) {
      const output = this.pageRoot?.querySelector(`[data-type-count="${type}"]`);
      if (output) output.textContent = String(count);
    }
  }

  cardMatchesSearch(card) {
    if (!this.searchQuery) return true;

    const searchableText = this.normalizeSearchText(card.dataset.searchText || '');
    const terms = this.searchQuery.split(/\s+/).filter(Boolean);
    return terms.every((term) => {
      const aliasGroup = SEARCH_ALIAS_GROUPS.find((aliases) => aliases.includes(term));
      const candidates = aliasGroup || [term];
      return candidates.some((candidate) => searchableText.includes(candidate));
    });
  }

  normalizeSearchText(value) {
    return String(value).normalize('NFKC').trim().toLocaleLowerCase();
  }

  setLoadingMessage(message) {
    if (this.loadingStatus) this.loadingStatus.textContent = message;
  }

  setFilterControlsLoading(isLoading) {
    this.setAttribute('aria-busy', String(isLoading));
    this.refreshExternalControls();
    for (const button of [...this.regionButtons, ...this.typeButtons]) {
      button.disabled = isLoading;
    }
  }

  setCardStatus(card, message) {
    const status = card.querySelector('[data-card-status]');
    if (status) status.textContent = message;
  }

  async navigateToDiscountCart(link) {
    if (this.cartNavigationPending) return;
    this.cartNavigationPending = true;
    link.setAttribute('aria-busy', 'true');

    try {
      // Complete the existing request, then replace earlier campaign codes using
      // a fresh update before opening the native discount/cart route.
      if (this.discountRequest) await this.discountRequest;
      if (!this.isConnected) return;
      await this.applyDiscount();
      if (this.isConnected) window.location.assign(link.href);
    } catch (error) {
      console.error('Unable to confirm the campaign discount before navigation.', error);
      if (this.isConnected) window.location.assign(link.href);
    } finally {
      this.cartNavigationPending = false;
      link.removeAttribute('aria-busy');
    }
  }

  applyDiscount() {
    this.refreshExternalControls();
    const discountCode = this.dataset.discountCode?.trim();
    if (!discountCode) {
      if (this.discountStatus) this.discountStatus.textContent = '';
      return Promise.resolve();
    }
    if (this.discountRequest) return this.discountRequest;

    const setStatus = (state) => {
      if (this.isConnected && this.discountStatus) {
        this.discountStatus.textContent = (this.dataset['discount' + state] || '').replaceAll('[code]', discountCode);
      }
    };
    setStatus('Loading');
    const controller = new AbortController();
    this.discountController = controller;

    const root = window.Shopify?.routes?.root || '/';
    this.discountRequest = fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ discount: discountCode }),
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((cart) => {
        const applied = cart.discount_codes?.some((discount) =>
          discount.code.toUpperCase() === discountCode.toUpperCase() && discount.applicable === true
        );
        setStatus(applied ? 'Applied' : cart.item_count === 0 ? 'Empty' : 'Unavailable');
        if (this.isConnected) this.dispatchEvent(new CartDiscountUpdateEvent({
          discountCodes: [{ code: discountCode }],
          promise: Promise.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(cart) }),
        }));
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        console.error('Unable to apply the group-buy discount.', error);
        setStatus('Error');
      })
      .finally(() => {
        this.discountRequest = null;
      });

    return this.discountRequest;
  }
}

class GroupBuyHeroCarousel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.dataset.initialized = 'true';
    this.track = this.querySelector('[data-carousel-track]');
    this.viewport = this.querySelector('.group-buy-carousel__viewport');
    this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
    this.status = this.querySelector('[data-carousel-status]');
    this.index = 0;
    this.pointerStart = null;
    this.suppressClickUntil = 0;
    this.autoplayEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1;
    this.intervalMs = Math.max(3000, Number(this.dataset.intervalMs || 5000));
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.addEventListener('click', this.handleClick, { signal });
    this.addEventListener('keydown', this.handleKeydown, { signal });
    this.addEventListener('pointerdown', this.handlePointerDown, { signal });
    this.addEventListener('pointerup', this.handlePointerUp, { signal });
    this.addEventListener('pointercancel', this.handlePointerCancel, { signal });
    this.addEventListener('mouseenter', this.handleMouseEnter, { signal });
    this.addEventListener('mouseleave', this.handleMouseLeave, { signal });
    this.addEventListener('focusin', this.handleFocusIn, { signal });
    this.addEventListener('focusout', this.handleFocusOut, { signal });
    document.addEventListener('visibilitychange', this.handleVisibilityChange, { signal });
    document.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });
    document.addEventListener('shopify:block:deselect', this.handleBlockDeselect, { signal });
    this.reducedMotionQuery.addEventListener?.('change', this.handleMotionPreferenceChange, { signal });

    this.setMotionPreference();
    this.selectSlide(0, { announce: false, restart: false });
    this.scheduleAutoplay();
  }

  disconnectedCallback() {
    this.clearAutoplay();
    this.abortController?.abort();
    delete this.dataset.initialized;
  }

  handleClick = (event) => {
    if (performance.now() < this.suppressClickUntil && event.target.closest('.group-buy-carousel__link')) {
      event.preventDefault();
      return;
    }

    const previousButton = event.target.closest('[data-carousel-previous]');
    if (previousButton) {
      this.selectSlide(this.index - 1);
      return;
    }

    const nextButton = event.target.closest('[data-carousel-next]');
    if (nextButton) {
      this.selectSlide(this.index + 1);
      return;
    }

    const dot = event.target.closest('[data-carousel-dot]');
    if (dot) this.selectSlide(Number(dot.dataset.carouselDot || 0));
  };

  handleKeydown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.selectSlide(this.index - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.selectSlide(this.index + 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.selectSlide(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.selectSlide(this.slides.length - 1);
    }
  };

  handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' || !this.viewport?.contains(event.target)) return;
    this.pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
  };

  handlePointerUp = (event) => {
    if (!this.pointerStart || event.pointerId !== this.pointerStart.id) return;
    const distanceX = event.clientX - this.pointerStart.x;
    const distanceY = event.clientY - this.pointerStart.y;
    this.pointerStart = null;

    if (Math.abs(distanceX) < 48 || Math.abs(distanceX) <= Math.abs(distanceY) * 1.2) return;
    this.suppressClickUntil = performance.now() + 350;
    this.selectSlide(distanceX < 0 ? this.index + 1 : this.index - 1);
  };

  handlePointerCancel = () => {
    this.pointerStart = null;
  };

  handleMouseEnter = () => {
    this.dataset.pausedByHover = 'true';
    this.clearAutoplay();
  };

  handleMouseLeave = () => {
    delete this.dataset.pausedByHover;
    this.scheduleAutoplay();
  };

  handleFocusIn = () => {
    this.dataset.pausedByFocus = 'true';
    this.clearAutoplay();
  };

  handleFocusOut = () => {
    window.setTimeout(() => {
      if (this.contains(document.activeElement)) return;
      delete this.dataset.pausedByFocus;
      this.scheduleAutoplay();
    }, 0);
  };

  handleVisibilityChange = () => {
    if (document.hidden) this.clearAutoplay();
    else this.scheduleAutoplay();
  };

  handleMotionPreferenceChange = () => {
    this.setMotionPreference();
    this.scheduleAutoplay();
  };

  handleBlockSelect = (event) => {
    const slide = event.target.closest?.('[data-carousel-slide]');
    if (!slide || !this.contains(slide)) return;
    this.dataset.pausedByEditor = 'true';
    this.selectSlide(this.slides.indexOf(slide), { restart: false });
    this.clearAutoplay();
  };

  handleBlockDeselect = (event) => {
    const slide = event.target.closest?.('[data-carousel-slide]');
    if (!slide || !this.contains(slide)) return;
    delete this.dataset.pausedByEditor;
    this.scheduleAutoplay();
  };

  setMotionPreference() {
    if (this.reducedMotionQuery.matches) this.dataset.reducedMotion = 'true';
    else delete this.dataset.reducedMotion;
  }

  selectSlide(index, { announce = true, restart = true } = {}) {
    if (!this.track || this.slides.length === 0) return;
    const nextIndex = ((index % this.slides.length) + this.slides.length) % this.slides.length;
    this.index = nextIndex;
    this.track.style.transform = `translate3d(-${nextIndex * 100}%, 0, 0)`;

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === nextIndex;
      slide.setAttribute('aria-hidden', String(!isActive));
      for (const link of slide.querySelectorAll('a')) link.tabIndex = isActive ? 0 : -1;
    });

    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === nextIndex;
      dot.classList.toggle('is-active', isActive);
      if (isActive) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    if (announce && this.status) this.status.textContent = `目前顯示第 ${nextIndex + 1} 張，共 ${this.slides.length} 張。`;
    if (restart) this.scheduleAutoplay();
  }

  canAutoplay() {
    return (
      this.autoplayEnabled &&
      !this.reducedMotionQuery.matches &&
      !document.hidden &&
      this.dataset.pausedByHover !== 'true' &&
      this.dataset.pausedByFocus !== 'true' &&
      this.dataset.pausedByEditor !== 'true'
    );
  }

  scheduleAutoplay() {
    this.clearAutoplay();
    if (!this.canAutoplay()) return;
    this.autoplayTimer = window.setTimeout(() => {
      this.selectSlide(this.index + 1, { announce: false, restart: false });
      this.scheduleAutoplay();
    }, this.intervalMs);
  }

  clearAutoplay() {
    if (!this.autoplayTimer) return;
    window.clearTimeout(this.autoplayTimer);
    this.autoplayTimer = null;
  }
}

if (!customElements.get('group-buy-collection')) {
  customElements.define('group-buy-collection', GroupBuyCollection);
}

if (!customElements.get('group-buy-hero-carousel')) {
  customElements.define('group-buy-hero-carousel', GroupBuyHeroCarousel);
}
